// Široké tabulky na telefonu (krok 13.7c–e, návrh docs/predani/2026-09-28-telefon-navrh.md § 5.4, úroveň T):
// tabulka, kterou na karty složit nejde (měsíční přehledy, KPI, evidence, ceníky…), se posouvá do strany jen ve
// svém rámečku (`.tabulka-obal` a dva vlastní, overflow-x: auto) a nad ní je věta „Pohodlnější na tabletu nebo
// počítači.“.
// Větu kreslí CSS (`.tabulka-obal[data-siroka='ano']::before` v komponenty.css) — tady se jen označí obal, jehož
// obsah se do šířky opravdu nevejde. Jednou pro celou aplikaci ze shellu, ať se 200 obalů tabulek nemusí měnit;
// úzká tabulka a tabulka složená do karet (`.tabulka-obal--karty`) větu nedostanou.
import { useEffect } from 'react';

/** Věta nad širokou tabulkou na telefonu — stejný text je v CSS (`content`), shodu hlídá CSS test. */
export const TEXT_SIROKA_TABULKA = 'Pohodlnější na tabletu nebo počítači.';

/**
 * Posuvné rámečky tabulek: společný `.tabulka-obal` a dva vlastní (Provozní deník — rozbory ČOV, zkoušky na alkohol,
 * historie; ISŘ › Školení — matice). Stejný seznam je v CSS věty (komponenty.css), shodu hlídá CSS test.
 */
export const RAMECKY_TABULEK = ['.tabulka-obal', '.denik__posun', '.isk__matice-obal'] as const;
const DOTAZ_RAMECKU = RAMECKY_TABULEK.join(', ');

/** Označí obal tabulky, který se do šířky nevejde (`data-siroka="ano"`), jinak značku odebere. */
export function oznacitObal(obal: HTMLElement): void {
  const siroka =
    !obal.classList.contains('tabulka-obal--karty') && obal.scrollWidth > obal.clientWidth + 1;
  if (siroka) {
    if (obal.getAttribute('data-siroka') !== 'ano') obal.setAttribute('data-siroka', 'ano');
  } else if (obal.hasAttribute('data-siroka')) {
    obal.removeAttribute('data-siroka');
  }
}

/**
 * Na telefonu (`aktivni`) sleduje obaly tabulek v dokumentu a značí široké. Nové obaly najde přes MutationObserver
 * (sekce se načítají líně), změnu šířky obalu i tabulky přes ResizeObserver. Mimo telefon nic nedělá a značky smaže.
 */
export function useOznacitSirokeTabulky(aktivni: boolean): void {
  useEffect(() => {
    if (!aktivni || typeof document === 'undefined') return undefined;
    const sledovane = new WeakSet<Element>();
    const rozmery =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver((zaznamy) => {
            for (const zaznam of zaznamy) {
              const obal = (zaznam.target as HTMLElement).closest<HTMLElement>(DOTAZ_RAMECKU);
              if (obal) oznacitObal(obal);
            }
          });
    let naplanovano = 0;
    const projit = () => {
      naplanovano = 0;
      for (const obal of document.querySelectorAll<HTMLElement>(DOTAZ_RAMECKU)) {
        if (!sledovane.has(obal)) {
          sledovane.add(obal);
          rozmery?.observe(obal);
          const tabulka = obal.firstElementChild;
          if (tabulka) rozmery?.observe(tabulka);
        }
        oznacitObal(obal);
      }
    };
    const zmeny =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver(() => {
            if (!naplanovano) naplanovano = window.requestAnimationFrame(projit);
          });
    zmeny?.observe(document.body, { childList: true, subtree: true });
    projit();
    return () => {
      zmeny?.disconnect();
      rozmery?.disconnect();
      if (naplanovano) window.cancelAnimationFrame(naplanovano);
      for (const obal of document.querySelectorAll('[data-siroka]')) {
        obal.removeAttribute('data-siroka');
      }
    };
  }, [aktivni]);
}
